var __typeError = (msg) => {
  throw TypeError(msg);
};
var __accessCheck = (obj, member, msg) => member.has(obj) || __typeError("Cannot " + msg);
var __privateGet = (obj, member, getter) => (__accessCheck(obj, member, "read from private field"), getter ? getter.call(obj) : member.get(obj));
var __privateAdd = (obj, member, value) => member.has(obj) ? __typeError("Cannot add the same private member more than once") : member instanceof WeakSet ? member.add(obj) : member.set(obj, value);
var __privateSet = (obj, member, value, setter) => (__accessCheck(obj, member, "write to private field"), setter ? setter.call(obj, value) : member.set(obj, value), value);
var __privateMethod = (obj, member, method) => (__accessCheck(obj, member, "access private method"), method);
var _intersectionObserver, _mutationObserver, _cachedRecommendations, _activeFetch, _dialog, _ProductRecommendations_instances, loadRecommendations_fn, fetchCachedRecommendations_fn, handleError_fn, handleEmptyRecommendations_fn;
import "./custom-dialog-MTSjyveX.js";
import { K as setInnerHTML, t as lazyReveal, q as activateInteractionAssets } from "./utilities-Da0sjanJ.js";
class ProductRecommendations extends HTMLElement {
  constructor() {
    super(...arguments);
    __privateAdd(this, _ProductRecommendations_instances);
    __privateAdd(this, _intersectionObserver, new IntersectionObserver(
      (entries, observer) => {
        var _a;
        if (!((_a = entries[0]) == null ? void 0 : _a.isIntersecting)) return;
        observer.disconnect();
        __privateMethod(this, _ProductRecommendations_instances, loadRecommendations_fn).call(this);
      },
      { rootMargin: "0px 0px 400px 0px" }
    ));
    __privateAdd(this, _mutationObserver, new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.target !== this || mutation.type !== "attributes") continue;
        if (mutation.attributeName === "class") continue;
        if (mutation.attributeName === "data-recommendations-performed" && this.dataset.recommendationsPerformed === "true")
          continue;
        if (mutation.attributeName === "data-error") continue;
        __privateMethod(this, _ProductRecommendations_instances, loadRecommendations_fn).call(this);
        break;
      }
    }));
    __privateAdd(this, _cachedRecommendations, {});
    __privateAdd(this, _activeFetch, null);
    __privateAdd(this, _dialog, null);
  }
  connectedCallback() {
    __privateSet(this, _dialog, this.closest("custom-dialog, cart-drawer"));
    if (__privateGet(this, _dialog)) {
      if (this.dataset.recommendationsPerformed === "false") {
        __privateMethod(this, _ProductRecommendations_instances, loadRecommendations_fn).call(this);
      }
    } else {
      this.classList.remove("hidden");
      __privateGet(this, _intersectionObserver).observe(this);
    }
    __privateGet(this, _mutationObserver).observe(this, { attributes: true });
  }
  disconnectedCallback() {
    var _a;
    __privateGet(this, _intersectionObserver).disconnect();
    __privateGet(this, _mutationObserver).disconnect();
    (_a = __privateGet(this, _activeFetch)) == null ? void 0 : _a.abort();
    if (__privateGet(this, _dialog)) {
      __privateSet(this, _dialog, null);
    }
  }
}
_intersectionObserver = new WeakMap();
_mutationObserver = new WeakMap();
_cachedRecommendations = new WeakMap();
_activeFetch = new WeakMap();
_dialog = new WeakMap();
_ProductRecommendations_instances = new WeakSet();
loadRecommendations_fn = function() {
  const { productId, recommendationsPerformed, sectionId, intent } = this.dataset;
  const id = this.id;
  if (!productId || !id) {
    this.classList.add("hidden");
    throw new Error("Product ID and an ID attribute are required");
  }
  if (recommendationsPerformed === "true") {
    return;
  }
  __privateMethod(this, _ProductRecommendations_instances, fetchCachedRecommendations_fn).call(this, productId, sectionId, intent).then((result) => {
    if (!result.success) {
      if (!Shopify.designMode) {
        __privateMethod(this, _ProductRecommendations_instances, handleError_fn).call(this, new Error(`Server returned ${result.status}`));
      }
      return;
    }
    const html = document.createElement("div");
    html.innerHTML = result.data || "";
    const recommendations = html.querySelector(`product-recommendations[id="${id}"]`);
    if ((recommendations == null ? void 0 : recommendations.innerHTML) && recommendations.innerHTML.trim().length) {
      this.dataset.recommendationsPerformed = "true";
      setInnerHTML(this, recommendations.innerHTML);
      lazyReveal(this);
      activateInteractionAssets(this);
      __privateMethod(this, _ProductRecommendations_instances, handleEmptyRecommendations_fn).call(this, recommendations);
    } else {
      __privateMethod(this, _ProductRecommendations_instances, handleError_fn).call(this, new Error("No recommendations available"));
    }
  }).catch((e) => {
    if (e.name === "AbortError") return;
    __privateMethod(this, _ProductRecommendations_instances, handleError_fn).call(this, e);
  });
};
fetchCachedRecommendations_fn = async function(productId, sectionId, intent) {
  var _a;
  const url = `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;
  const cachedResponse = __privateGet(this, _cachedRecommendations)[url];
  if (cachedResponse) {
    return { success: true, data: cachedResponse };
  }
  (_a = __privateGet(this, _activeFetch)) == null ? void 0 : _a.abort();
  __privateSet(this, _activeFetch, new AbortController());
  try {
    const response = await fetch(url, { signal: __privateGet(this, _activeFetch).signal });
    if (!response.ok) {
      return { success: false, status: response.status };
    }
    const text = await response.text();
    __privateGet(this, _cachedRecommendations)[url] = text;
    return { success: true, data: text };
  } finally {
    __privateSet(this, _activeFetch, null);
  }
};
handleError_fn = function(error) {
  this.classList.add("hidden");
  this.dataset.error = "Error loading product recommendations";
};
handleEmptyRecommendations_fn = function(recommendations) {
  if (!recommendations) return;
  if (recommendations.hasAttribute("data-empty-result")) {
    const isEmptyResult = recommendations.dataset.emptyResult === "true" ? true : false;
    if (isEmptyResult) {
      this.classList.add("hidden");
      this.dataset.error = "No recommendations available";
    } else {
      this.classList.remove("hidden");
    }
  }
};
if (!customElements.get("product-recommendations")) {
  customElements.define("product-recommendations", ProductRecommendations);
}
